import path from 'path'

import _ from 'lodash'
import axios from 'axios'
import bluebird from 'bluebird'
import moment from 'moment-timezone'
import uuid from 'uuid'
import { message } from 'antd'
import { QUOTE_MODEL } from '@Builder/QuoteDetailPage/constants'
import { BACK_URL_OBJECT_FOR } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'
import { DocumentNode } from 'graphql'
import { ThunkDispatch } from 'redux-thunk'
import { ApolloClient, InternalRefetchQueryDescriptor } from '@apollo/client'
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'
import { TableDataType } from '@Builder/QuoteDetailPage/selectors'
import { SortOrder } from 'antd/lib/table/interface'
import { TableDataTypeModel } from '@Builder/QuoteDetailPage/types'

import { encodeForHeader } from '~/lib/encodeForHeader'
import { isBlockedFileName } from '~/lib/fileExtensions'
import { PROCESSID_IM } from '~/lib/partsConstants'
import logger from '~/lib/logger'
import isCADFile from '~/lib/isCADFile'
import { REGISTERED, FAILED } from '~/lib/partStates'
import LocalStorageWrapper from '~/lib/LocalStorageWrapper'
import {
    UPLOAD_BLOCKED_ATTACHMENT_ERROR,
    UPLOAD_BLOCKED_SOME_ATTACHMENTS_ERROR,
} from '~/lib/flashMsgs'
import { ADMIN_PATH, QMR_PATH } from '~/redux-modules/preferences'
import { AttachmentCreateType, QuoteType } from '~/__generated__/globalTypes'
import {
    QuoteDetailsDocument,
    CreateQuoteAttachmentRevisionFilesTabDocument,
    MarkQuoteAttachmentRevisionUploadedFilesTabDocument,
    CreateQuoteModelRevisionDocument,
    FilesTabFinalizeUploadAndProcessPartDocument,
    QuoteDetailsQueryVariables,
    CreateQuoteModelRevisionMutation,
    CreateQuoteModelRevisionMutationVariables,
    QuoteModelRevisionUploadInput,
    CreateQuoteAttachmentRevisionFilesTabMutation,
    CreateQuoteAttachmentRevisionFilesTabMutationVariables,
    BuilderConfigurablePostProcessesDocument,
} from '~/gql/graphql'
import {
    coreQuoteDetails_quote_quoteModels_items,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_modelConfiguration_productionLeadTime as ProductionLeadTime,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_mold,
} from '~/redux-modules/query-refactor/__generated__/coreQuoteDetails'

import {
    getQuantity,
    getQuoteModelRevisionName,
    getPriceInCents,
    getProcessName,
    getProductionDays,
    isAConfiguredQuoteModel,
} from './selectors'
import {
    createProductionLeadTimesQuery,
    createUserEnteredMaterialsQuery,
} from './query'
import { quoteLevelLeadTimesWithShippingOptionsQuery } from './QuoteLevelLeadTime/query'

const storage = new LocalStorageWrapper()

export const getFamilyMolds = (
    quoteModelItems: coreQuoteDetails_quote_quoteModels_items[] = [],
) => {
    return (
        quoteModelItems?.reduce<
            {
                fileName: string
                mold: coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_mold
            }[]
        >((molds, quoteModelItem) => {
            const moldsAlreadyAdded = molds?.filter(mold => {
                return (
                    quoteModelItem?.selectedQuoteModelRevision.mold?.id ===
                    mold?.mold?.id
                )
            })

            if (moldsAlreadyAdded?.length === 0) {
                const { fileName, mold } =
                    quoteModelItem.selectedQuoteModelRevision

                if (mold) {
                    molds.push({ fileName, mold })
                }
            }
            return molds
        }, []) || []
    )
}

export type MoldTableDataType = {
    fileName: string
    mold: coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_mold
}

interface GetRevisionHistoryUrlOptions {
    isObo: boolean
    isLocatedInAdminPanel?: boolean
    oboUserId?: number
    qmrId?: string
    quoteId: string
}

export const getRevisionHistoryUrl = ({
    isObo,
    isLocatedInAdminPanel,
    oboUserId,
    qmrId,
    quoteId,
}: GetRevisionHistoryUrlOptions) => {
    if (isObo) {
        return {
            backUrl: BACK_URL_OBJECT_FOR.ADMIN_SEARCH,
            url: `${ADMIN_PATH}/user/${oboUserId}/quotes/${quoteId}/qmr/${qmrId}/history`,
        }
    } else if (isLocatedInAdminPanel) {
        return {
            backUrl: BACK_URL_OBJECT_FOR.ADMIN_PANEL,
            url: `/admin/quote-model-revision/${qmrId}/history`,
        }
    }

    return {
        backUrl: BACK_URL_OBJECT_FOR.USER,
        url: `${QMR_PATH}/${qmrId}/history`,
    }
}

const fullQuoteDetailsQuery = (
    quoteId: string,
): {
    query: DocumentNode
    variables: QuoteDetailsQueryVariables
} => ({
    query: QuoteDetailsDocument,
    variables: { id: quoteId },
})

const createQuoteLevelLeadTimesAndPricesQuery = () =>
    quoteLevelLeadTimesWithShippingOptionsQuery

const createPostProcessQuery = (materialId?: string, processId?: string) => ({
    query: BuilderConfigurablePostProcessesDocument,
    variables: {
        materialId,
        processId,
    },
    skip: !materialId, // seems not working here as well
})

export function configurationRefetches(
    ids: string[],
    quoteId: string,
    materialId?: string,
    processId?: string,
    skipQLLT = false,
) {
    const refetchQueries: InternalRefetchQueryDescriptor[] = [
        createUserEnteredMaterialsQuery(),
        ...ids.map(qmrId => createProductionLeadTimesQuery(qmrId)),

        ...(materialId ? [createPostProcessQuery(materialId, processId)] : []),
    ]

    if (!skipQLLT) {
        refetchQueries.push(createQuoteLevelLeadTimesAndPricesQuery())
    }

    return refetchQueries
}
export function applyConfigurationRefetches(
    ids: string[],
    quoteId?: string,
    materialId?: string,
    processId?: string,
    skipQLLT = false,
    isQueryDetailsQueryRefactorEnabled = false,
) {
    const refetchQueries: InternalRefetchQueryDescriptor[] = [
        createUserEnteredMaterialsQuery(),
        ...ids.map(qmrId => createProductionLeadTimesQuery(qmrId)),
        ...(materialId ? [createPostProcessQuery(materialId, processId)] : []),
    ]

    if (!skipQLLT && typeof quoteId !== 'undefined') {
        refetchQueries.push(createQuoteLevelLeadTimesAndPricesQuery())
    }
    if (!isQueryDetailsQueryRefactorEnabled && typeof quoteId !== 'undefined') {
        refetchQueries.push(fullQuoteDetailsQuery(quoteId))
    }

    return refetchQueries
}

interface FileWithMetadata
    extends File,
        Pick<
            QuoteModelRevisionUploadInput,
            'referenceId' | 'contentType' | 'units'
        > {
    fileHandle: FileWithMetadata
}

// this function mutates file object, which is bad. But changing it now might brake existing functionality.
export function hydrateMetadata(
    file: File,
    units: string | null = null,
): FileWithMetadata {
    return Object.assign(file, {
        referenceId: uuid.v4(),
        fileHandle: file as FileWithMetadata,
        contentType: file.type || 'application/octet-stream',
        units,
    })
}

interface FileUploadData {
    file: FileWithMetadata
    quoteId?: string
    units: string
    userId: number | null
}

export async function createQuoteModelRevision(
    { file, units, quoteId, userId }: FileUploadData,
    client?: ApolloClient<any>,
) {
    try {
        const result = await client?.mutate<
            CreateQuoteModelRevisionMutation,
            CreateQuoteModelRevisionMutationVariables
        >({
            mutation: CreateQuoteModelRevisionDocument,
            variables: {
                uploadMetadata: {
                    referenceId: file.referenceId,
                    contentType: file.contentType,
                    fileName: file.name,
                    fileSize: file.size,
                    units,
                },
                quoteId,
                quoteModelId: null,
                setAsSelectedForQuoteModel: true,
                userId,
            },
        })

        //return metadata to be used in uploadStagedPartFiles
        return {
            referenceId: file.referenceId,
            quoteId,
            quoteModelRevisionId: 'data.createQuoteModelRevision.id',
            fileName: file.name,
            fileSize: file.fileHandle.size,
            postUrl: result?.data?.createQuoteModelRevision.uploadUrl,
            partId: result?.data?.createQuoteModelRevision.partRevision.partId,
        }
    } catch (err) {
        logger.exception(err, {
            extra: {
                info: 'Failed to create quoteModelRevision during upload',
                quoteId,
                fileName: file.name,
                fileSize: file.size,
            },
        })
        message.error(`Failed to upload part, ${file.name}`)
        throw err
    }
}

interface MarkQuoteAttachmentRevisionAsUploadedData {
    partUploadMetadata: AttachmentUploadMetadata
    quoteId?: string
}

interface MarkQuoteAttachmentRevisionAsUploadedOptions {
    client?: ApolloClient<any>
    refetchQuery: MutationBaseOptions['refetchQueries']
}

export function markQuoteAttachmentRevisionAsUploaded(
    data: MarkQuoteAttachmentRevisionAsUploadedData,
    options: MarkQuoteAttachmentRevisionAsUploadedOptions,
) {
    const { partUploadMetadata, quoteId } = data
    const { client, refetchQuery } = options

    try {
        return client?.mutate({
            mutation: MarkQuoteAttachmentRevisionUploadedFilesTabDocument,
            variables: {
                id: partUploadMetadata.quoteAttachmentRevisionId,
            },
            refetchQueries: refetchQuery,
        })
    } catch (err) {
        logger.exception(err, {
            extra: {
                info: 'Failed to mark quote attachment uploaded',
                quoteId,
                fileName: partUploadMetadata.fileName,
                quoteAttachmentRevisionId:
                    partUploadMetadata.quoteAttachmentRevisionId,
            },
        })
        message.error(
            `${partUploadMetadata.fileName} failed to complete the upload`,
        )
        throw err
    }
}

interface CreateQuoteAttachmentRevisionData {
    file: FileWithMetadata
    quoteId?: string
    isDfmReportFileType: boolean
}

export async function createQuoteAttachmentRevision(
    data: CreateQuoteAttachmentRevisionData,
    client?: ApolloClient<any>,
) {
    const { file, quoteId, isDfmReportFileType } = data

    try {
        const result = await client?.mutate<
            CreateQuoteAttachmentRevisionFilesTabMutation,
            CreateQuoteAttachmentRevisionFilesTabMutationVariables
        >({
            mutation: CreateQuoteAttachmentRevisionFilesTabDocument,
            variables: {
                uploadMetadata: {
                    mimeType: file.contentType,
                    extension: path.extname(file.name),
                    fileName: file.name,
                    type: AttachmentCreateType.user_uploaded,
                },
                quoteId,
                attachmentType: isDfmReportFileType ? 'dfm-report' : null,
                quoteAttachmentId: null,
                productAttachmentId: null,
            },
        })

        const quoteAttachmentRevisionId =
            result?.data?.createQuoteAttachmentRevision.id
        const uploadUrl =
            result?.data?.createQuoteAttachmentRevision.attachment?.uploadUrl ||
            result?.data?.createQuoteAttachmentRevision.asset?.uploadUrl

        return {
            quoteAttachmentRevisionId,
            contentType: file.type,
            fileHandle: file,
            fileName: file.name,
            uploadUrl,
        }
    } catch (err) {
        logger.exception(err, {
            extra: {
                info: 'Failed to create quoteAttachmentRevision during upload',
                quoteId,
                fileName: file.name,
            },
        })

        throw err
    }
}

type AttachmentUploadMetadata = Awaited<
    ReturnType<typeof createQuoteAttachmentRevision>
> &
    Pick<FileWithMetadata, 'fileHandle' | 'contentType'>

type PartUploadMetadata = Awaited<ReturnType<typeof createQuoteModelRevision>> &
    Pick<FileWithMetadata, 'fileHandle' | 'contentType'>

export function uploadPartFile(partUploadMetadata: PartUploadMetadata) {
    const { contentType, fileHandle, postUrl } = partUploadMetadata

    if (!postUrl) {
        return
    }

    return axios
        .put(postUrl, fileHandle, {
            withCredentials: false,
            headers: { 'Content-Type': contentType },
        })
        .catch(err => {
            logger.exception(err, {
                extra: {
                    info: 'Failed to upload part to s3',
                    fileHandle,
                    postUrl,
                },
            })

            throw err
        })
}

export function uploadAttachmentFile(uploadMetadata: AttachmentUploadMetadata) {
    const { contentType, uploadUrl, fileHandle, fileName } = uploadMetadata

    if (!uploadUrl) {
        return
    }

    return axios
        .put(uploadUrl, fileHandle, {
            withCredentials: false,
            headers: {
                'Content-Type': contentType,
                'Content-Disposition': `attachment; filename*=UTF-8''${encodeForHeader(
                    fileName,
                )}`,
            },
        })
        .catch(err => {
            logger.exception(err, {
                extra: {
                    info: 'Failed to upload attachment to s3',
                    fileName,
                    uploadUrl,
                },
            })
            throw err
        })
}

export async function managePartUpload<F extends File>(
    data: { file: F; quoteId?: string; units: string; userId: number | null },
    options: { client?: ApolloClient<any> },
) {
    const { file, quoteId, units, userId } = data
    const { client } = options

    const fullFile = hydrateMetadata(file, units)

    // since hydrateMetadata mutates file object need to cast to type
    const partUploadMetadata = (await createQuoteModelRevision(
        { file: file as unknown as FileWithMetadata, units, quoteId, userId },
        client,
    )) as PartUploadMetadata

    Object.assign(partUploadMetadata, {
        fileHandle: fullFile,
        contentType: fullFile.contentType,
    })

    await uploadPartFile(partUploadMetadata)

    await client?.mutate({
        mutation: FilesTabFinalizeUploadAndProcessPartDocument,
        variables: {
            partId: partUploadMetadata.partId,
        },
    })
}

export function treatConfigAsLegacyIm({
    modelConfiguration,
    isImEnabled,
    quoteType,
}: {
    modelConfiguration?: TableDataTypeModel['selectedQuoteModelRevision']['modelConfiguration']
    isImEnabled: boolean
    quoteType?: string
}) {
    return (
        isImEnabled &&
        quoteType !== QuoteType.im &&
        modelConfiguration?.process?.id === PROCESSID_IM
    )
}

interface ManageAttachmentUploadOptions {
    client?: ApolloClient<any>
    refetchQuery?: MutationBaseOptions['refetchQueries']
}

export async function manageAttachmentUpload<F extends File>(
    data: { file: F; quoteId?: string; isDfmReportFileType: boolean },
    options: ManageAttachmentUploadOptions,
) {
    const { file, quoteId, isDfmReportFileType } = data
    const { client, refetchQuery } = options

    const fullFile = hydrateMetadata(file)
    const partUploadMetadata = await createQuoteAttachmentRevision(
        { file: fullFile, quoteId, isDfmReportFileType },
        client,
    )

    Object.assign(partUploadMetadata, {
        fileHandle: fullFile,
        contentType: fullFile.contentType,
    })

    await uploadAttachmentFile(partUploadMetadata)
    await markQuoteAttachmentRevisionAsUploaded(
        { partUploadMetadata, quoteId },
        { client, refetchQuery },
    )
}

export function uploadDfmReportFiles<F extends File>(
    data: { files: F[]; quoteId?: string },
    options: ManageAttachmentUploadOptions,
) {
    const { files, quoteId } = data

    // according to bluebird doc http://bluebirdjs.com/docs/api/promise.map.html
    // map - was created to replace native promisesArr.push(promise) + Promise.all(promisesArr)
    // typescript is arguing about mismatch types when we are trying to pass result of bluebird.map to Promise.all, since result of blubierd.map isn't array - it's class Bluebird
    return bluebird.map(
        files,
        async file => {
            try {
                return await manageAttachmentUpload(
                    { file, quoteId, isDfmReportFileType: true },
                    options,
                )
            } catch (err) {
                return true
            }
        },
        { concurrency: 10 },
    )
}

interface UploadQuoteFilesData<F extends File> {
    files: F[]
    quoteId?: string
    units: string
    userIdObo?: number
}

interface UploadQuoteFilesOptions {
    dispatch: ThunkDispatch<any, any, any>
    client?: ApolloClient<any>
}

export function uploadQuoteFiles<F extends File>(
    data: UploadQuoteFilesData<F>,
    options: UploadQuoteFilesOptions,
) {
    const { files, quoteId, units, userIdObo } = data
    const blockedFiles = files.filter(file => isBlockedFileName(file.name))
    const userId = userIdObo ? userIdObo : null

    if (blockedFiles.length === files.length) {
        message.error(UPLOAD_BLOCKED_ATTACHMENT_ERROR)
    } else if (blockedFiles.length) {
        message.warning(UPLOAD_BLOCKED_SOME_ATTACHMENTS_ERROR)
    }

    // according to bluebird doc http://bluebirdjs.com/docs/api/promise.map.html
    // map - was created to replace native promisesArr.push(promise) + Promise.all(promisesArr)
    // typescript is arguing about mismatch types when we are trying to pass result of bluebird.map to Promise.all, since result of blubierd.map isn't array - it's class Bluebird
    return bluebird.map(
        files,
        async file => {
            if (isBlockedFileName(file.name)) {
                return true
            }
            try {
                return isCADFile(file)
                    ? await managePartUpload(
                          { file, quoteId, units, userId },
                          options,
                      )
                    : await manageAttachmentUpload(
                          { file, quoteId, isDfmReportFileType: false },
                          options,
                      )
            } catch (err) {
                return true
            }
        },
        { concurrency: 10 },
    )
}

export function recordHasError<
    T extends {
        item: {
            __typename: string
            selectedQuoteModelRevision?: {
                partRevision: {
                    analysisErrorCode: string | null
                }
            }
        }
    },
>(record: T) {
    // export function recordHasError(record: TableDataType) {
    const hasErrorCode = Boolean(
        record?.item?.__typename === QUOTE_MODEL &&
            record?.item?.selectedQuoteModelRevision?.partRevision
                ?.analysisErrorCode != null,
    )

    if (hasErrorCode) {
        return true
    }

    const recordStatus = (record?.item as TableDataTypeModel)
        ?.selectedQuoteModelRevision?.partRevision?.status

    const partHasErrorStatus =
        recordStatus === REGISTERED || recordStatus === FAILED

    if (!partHasErrorStatus) {
        return false
    }

    const qmrCreatedAt = _.get(
        record,
        'item.selectedQuoteModelRevision.createdAt',
    )
    const timeoutSeconds = 6
    const maxErrorTimeout = moment(qmrCreatedAt).add(timeoutSeconds, 'seconds')

    return moment().isAfter(maxErrorTimeout)
}

export const uniqueProductionLeadTimes = <
    T extends {
        items: {
            selectedQuoteModelRevision: {
                modelConfiguration: {
                    productionLeadTime: ProductionLeadTime | null
                } | null
            } | null
        }[]
    },
>(
    quoteModels?: T,
) => {
    const leadTimes = quoteModels?.items?.map(quoteModel => {
        return quoteModel.selectedQuoteModelRevision?.modelConfiguration
            ?.productionLeadTime
    })

    return _.compact(_.uniqBy(leadTimes, 'productionDays'))
}

export const getLocalStorageItem = (key: string) => storage.getItem(key)

export const setLocalStorageItem = (key: string, value: any) =>
    storage.setItem(key, value)

let sortCriteria: string
let sortCache: Record<string, number>

const checkSorterCache = (criteria: string, key: string) => {
    if (sortCriteria === criteria) {
        if (sortCache?.[key]) {
            return sortCache[key]
        }
    } else {
        sortCriteria = criteria
        sortCache = {}
    }
    return null
}

const alphabeticAscendingSort = (a?: string | null, b?: string | null) => {
    // todo: revisit sorting logic to make it handle null and undefined cases. For now just cast to string to not interfere in sorting logic
    if ((a as string) < (b as string)) {
        return -1
    } else if ((a as string) > (b as string)) {
        return 1
    } else {
        return 0
    }
}

const optionalNumericDescendingSort = (a: number | null, b: number | null) => {
    if (a === b) {
        return 0
    }
    const aIsNumber = typeof a === 'number'
    const bIsNumber = typeof b === 'number'

    if (aIsNumber) {
        if (bIsNumber) {
            return a < b ? 1 : -1
        } else {
            return -1
        }
    } else {
        if (bIsNumber) {
            return 1
        } else {
            return 0
        }
    }
}

export const quoteSorters = () => {
    const configuredModelSort = (a: TableDataType, b: TableDataType) => {
        const aIsAConfiguredModel = isAConfiguredQuoteModel(a)
        const bIsAConfiguredModel = isAConfiguredQuoteModel(b)

        if (aIsAConfiguredModel && !bIsAConfiguredModel) {
            return -1
        } else if (bIsAConfiguredModel && !aIsAConfiguredModel) {
            return 1
        } else {
            return 0
        }
    }

    const nameSort = (a: TableDataType, b: TableDataType) => {
        const aName = getQuoteModelRevisionName(a)
        const bName = getQuoteModelRevisionName(b)

        return alphabeticAscendingSort(
            aName?.toLowerCase(),
            bName?.toLowerCase(),
        )
    }

    const configurationSort = (a: TableDataType, b: TableDataType) => {
        const aProcessName = getProcessName(a)
        const bProcessName = getProcessName(b)

        return alphabeticAscendingSort(aProcessName, bProcessName)
    }

    const productionSpeedSort = (a: TableDataType, b: TableDataType) => {
        const aProductionDays = getProductionDays(a)
        const bProductionDays = getProductionDays(b)

        return optionalNumericDescendingSort(aProductionDays, bProductionDays)
    }

    const quantitySort = (a: TableDataType, b: TableDataType) => {
        const aQuantity = getQuantity(a)
        const bQuantity = getQuantity(b)

        return optionalNumericDescendingSort(aQuantity, bQuantity)
    }

    const totalPriceSort = (a: TableDataType, b: TableDataType) => {
        const aPrice = getPriceInCents(a)
        const bPrice = getPriceInCents(b)

        return optionalNumericDescendingSort(aPrice, bPrice)
    }

    type SortFn = (a: TableDataType, b: TableDataType) => number

    const applySorts = (
        sorts: SortFn[],
        a: TableDataType,
        b: TableDataType,
    ) => {
        let sortOrder = 0

        for (let i = 0; i < sorts.length; ++i) {
            const currentSort: SortFn = sorts[i]

            sortOrder = currentSort(a, b)
            if (sortOrder) {
                break
            }
        }
        return sortOrder
    }

    return {
        name: (a: TableDataType, b: TableDataType, order?: SortOrder) => {
            const COLUMN = 'name'
            const criteria = `${COLUMN}-${order}`
            const key = `${a?.item?.id}-${b?.item?.id}`

            const cachedValue = checkSorterCache(criteria, key)

            if (cachedValue) {
                return cachedValue
            }

            const sorts = [
                nameSort,
                configuredModelSort,
                configurationSort,
                productionSpeedSort,
                quantitySort,
                totalPriceSort,
            ]

            sortCache[key] = applySorts(sorts, a, b)
            return sortCache[key]
        },
        configuration: (
            a: TableDataType,
            b: TableDataType,
            order?: SortOrder,
        ) => {
            const COLUMN = 'configuration'
            const criteria = `${COLUMN}-${order}`
            const key = `${a?.item?.id}-${b?.item?.id}`

            const cachedValue = checkSorterCache(criteria, key)

            if (cachedValue) {
                return cachedValue
            }

            const sorts = [
                configuredModelSort,
                configurationSort,
                nameSort,
                productionSpeedSort,
                quantitySort,
                totalPriceSort,
            ]

            sortCache[key] = applySorts(sorts, a, b)
            return sortCache[key]
        },
        productionSpeed: (
            a: TableDataType,
            b: TableDataType,
            order?: SortOrder,
        ) => {
            const COLUMN = 'productionSpeed'
            const criteria = `${COLUMN}-${order}`
            const key = `${a?.item?.id}-${b?.item?.id}`

            const cachedValue = checkSorterCache(criteria, key)

            if (cachedValue) {
                return cachedValue
            }

            const sorts = [
                configuredModelSort,
                productionSpeedSort,
                nameSort,
                configurationSort,
                quantitySort,
                totalPriceSort,
            ]

            sortCache[key] = applySorts(sorts, a, b)
            return sortCache[key]
        },
        quantity: (a: TableDataType, b: TableDataType, order?: SortOrder) => {
            const COLUMN = 'quantity'
            const criteria = `${COLUMN}-${order}`
            const key = `${a?.item?.id}-${b?.item?.id}`

            const cachedValue = checkSorterCache(criteria, key)

            if (cachedValue) {
                return cachedValue
            }

            const sorts = [
                configuredModelSort,
                quantitySort,
                nameSort,
                configurationSort,
                productionSpeedSort,
                totalPriceSort,
            ]

            sortCache[key] = applySorts(sorts, a, b)
            return sortCache[key]
        },
        price: (a: TableDataType, b: TableDataType, order?: SortOrder) => {
            const COLUMN = 'price'
            const criteria = `${COLUMN}-${order}`
            const key = `${a?.item?.id}-${b?.item?.id}`

            const cachedValue = checkSorterCache(criteria, key)

            if (cachedValue) {
                return cachedValue
            }

            const sorts = [
                configuredModelSort,
                totalPriceSort,
                nameSort,
                configurationSort,
                productionSpeedSort,
                quantitySort,
            ]

            sortCache[key] = applySorts(sorts, a, b)
            return sortCache[key]
        },
    }
}

export const getProductionLeadTimeDays = (
    leadTimes: ProductionLeadTime[] = [],
) =>
    leadTimes
        .map(leadTime => leadTime?.productionDays || 0)
        .sort((a, b) => a - b)
